import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fmtIstTime } from '../lib/datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// localStorage keys for "remember me on this device"
const LS_EMP = 'checkin_emp_id';
const LS_NAME = 'checkin_name';

const BREAK_LABELS = {
  tea_morning: '☕ Morning Tea (15 min)',
  lunch: '🍱 Lunch (45 min)',
  tea_evening: '☕ Evening Tea (15 min)',
};

const fmtTime = fmtIstTime;

function fmtDuration(min) {
  if (!min) return '0 min';
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h ${m}m` : `${m} min`;
}

const CheckInPage = () => {
  const [empId, setEmpId] = useState(localStorage.getItem(LS_EMP) || '');
  const [name, setName] = useState(localStorage.getItem(LS_NAME) || '');
  const [data, setData] = useState(null);   // status response
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSelfie, setShowSelfie] = useState(null); // null | 'in' | 'out'
  const [selfieData, setSelfieData] = useState('');
  const [gps, setGps] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopCamera(), []);

  // Capture GPS on first valid status
  useEffect(() => {
    if (!data || gps) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 },
    );
  }, [data, gps]);

  const fetchStatus = async () => {
    setError('');
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/checkin/status`, { params: { emp_id: empId } });
      setData(r.data);
      localStorage.setItem(LS_EMP, empId);
      localStorage.setItem(LS_NAME, name);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setData(null);
    } finally { setBusy(false); }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 640 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      setError('Camera access denied. ' + e.message);
      setShowSelfie(null);
    }
  };

  const capture = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = 320; c.height = 426;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    setSelfieData(c.toDataURL('image/jpeg', 0.72));
    stopCamera();
  };

  const openSelfieFor = async (action) => {
    setShowSelfie(action);
    setSelfieData('');
    // Defer camera start to next tick so video element mounts
    setTimeout(startCamera, 100);
  };

  const submitWithSelfie = async (action) => {
    if (!selfieData) { setError('Please capture a selfie first'); return; }
    setBusy(true); setError('');
    try {
      const body = { emp_id: empId, name, selfie: selfieData, gps };
      const url = `${API_URL}/api/checkin/${action === 'in' ? 'in' : 'out'}`;
      const r = await axios.post(url, body);
      alert(r.data.message);
      setShowSelfie(null); setSelfieData(''); stopCamera();
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const doBreak = async (action, type) => {
    // UX guard: confirm before STARTING a break (admin's ask). Skip on end / when timer already open.
    if (action === 'start') {
      const TYPE_LABEL = { tea_morning: 'Morning Tea ☕', lunch: 'Lunch 🍱', tea_evening: 'Evening Tea 🍵' };
      const label = TYPE_LABEL[type] || type;
      const ok = window.confirm(`Are you sure you want to take ${label} break?\n\nYou'll be marked unavailable until you click "End ${label} break" again.`);
      if (!ok) return;
    }
    setBusy(true); setError('');
    try {
      const url = `${API_URL}/api/checkin/break/${action}`;
      const r = await axios.post(url, { emp_id: empId, name, type });
      alert(r.data.message);
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // ---------------------- RENDER ----------------------
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#334155 100%)' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-7 space-y-4">
          <div className="text-center mb-3">
            <div className="text-3xl font-black tracking-tight" style={{ color: '#0f172a' }}>DEXTRA</div>
            <div className="text-xs uppercase tracking-widest mt-1" style={{ color: '#64748b' }}>Daily Attendance</div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold" style={{ color: '#475569' }}>Employee ID</label>
            <input value={empId} onChange={(e) => setEmpId(e.target.value.toUpperCase())} placeholder="EMP001"
              className="w-full border-2 rounded-lg px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:border-blue-500"
              style={{ borderColor: '#e2e8f0' }} data-testid="emp-id-input" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold" style={{ color: '#475569' }}>Your Full Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="As per HR records"
              className="w-full border-2 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
              style={{ borderColor: '#e2e8f0' }} data-testid="name-input" />
          </div>
          {error && (
            <div className="text-xs px-3 py-2 rounded-md" style={{ background: '#fef2f2', color: '#b91c1c' }}>{error}</div>
          )}
          <button onClick={fetchStatus} disabled={busy || !empId || !name} data-testid="continue-btn"
            className="w-full py-3 rounded-lg text-white font-bold text-sm uppercase tracking-wide disabled:opacity-50"
            style={{ background: '#0f172a' }}>
            {busy ? 'Loading…' : 'Continue →'}
          </button>
          <p className="text-[10px] text-center" style={{ color: '#94a3b8' }}>
            Office Hours: 10:30 AM – 7:30 PM. Tea breaks 15 min × 2, Lunch 45 min.
          </p>
        </div>
      </div>
    );
  }

  // ===== Selfie capture modal =====
  if (showSelfie) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0f172a' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
          <div className="text-center">
            <div className="text-base font-bold" style={{ color: '#0f172a' }}>
              📸 Capture Selfie for {showSelfie === 'in' ? 'Check IN' : 'Check OUT'}
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: '#64748b' }}>Look at the camera and tap Capture</div>
          </div>
          <div className="rounded-lg overflow-hidden bg-black aspect-[3/4] flex items-center justify-center">
            {selfieData ? (
              <img src={selfieData} alt="captured" className="w-full h-full object-cover" />
            ) : (
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="selfie-video" />
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />
          {error && <div className="text-xs px-3 py-2 rounded-md" style={{ background: '#fef2f2', color: '#b91c1c' }}>{error}</div>}
          <div className="flex gap-2">
            <button onClick={() => { setShowSelfie(null); stopCamera(); setSelfieData(''); }}
              className="flex-1 py-2.5 rounded-lg font-semibold text-sm border" style={{ borderColor: '#e2e8f0', color: '#475569' }}>
              Cancel
            </button>
            {!selfieData ? (
              <button onClick={capture} data-testid="capture-btn"
                className="flex-1 py-2.5 rounded-lg font-bold text-sm text-white" style={{ background: '#0f172a' }}>
                📷 Capture
              </button>
            ) : (
              <>
                <button onClick={() => { setSelfieData(''); startCamera(); }}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-sm border" style={{ borderColor: '#e2e8f0' }}>
                  Retake
                </button>
                <button onClick={() => submitWithSelfie(showSelfie)} disabled={busy} data-testid="confirm-checkin-btn"
                  className="flex-1 py-2.5 rounded-lg font-bold text-sm text-white" style={{ background: showSelfie === 'in' ? '#15803d' : '#b45309' }}>
                  {busy ? 'Sending…' : (showSelfie === 'in' ? '✓ Confirm IN' : '✓ Confirm OUT')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== Main dashboard =====
  const rec = data.record;
  const action = data.next_action; // check_in | working | break_end:xxx | done

  return (
    <div className="min-h-screen p-4" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#334155 100%)' }}>
      <div className="max-w-md mx-auto space-y-3">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: '#94a3b8' }}>Welcome</div>
              <div className="text-lg font-bold" style={{ color: '#0f172a' }}>{data.employee.name}</div>
              <div className="text-xs font-mono" style={{ color: '#64748b' }}>{data.employee.emp_id} • {data.today}</div>
            </div>
            <button onClick={() => { localStorage.removeItem(LS_EMP); localStorage.removeItem(LS_NAME); setData(null); setEmpId(''); setName(''); }}
              className="text-[10px] underline" style={{ color: '#94a3b8' }} data-testid="switch-user-btn">
              Switch user
            </button>
          </div>
          {data.wfh_approved && (
            <div className="mt-2 text-[10px] px-2 py-1 rounded inline-block" style={{ background: '#dbeafe', color: '#1e40af' }}>
              🏠 WFH approved for today
            </div>
          )}
        </div>

        {/* State card */}
        {action === 'check_in' && (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center space-y-3">
            <div className="text-base font-bold" style={{ color: '#0f172a' }}>Ready to start your day?</div>
            <div className="text-xs" style={{ color: '#64748b' }}>Tap below to capture your selfie and check in</div>
            <button onClick={() => openSelfieFor('in')} disabled={busy} data-testid="check-in-btn"
              className="w-full py-4 rounded-xl text-white font-bold text-base uppercase tracking-wide"
              style={{ background: '#15803d' }}>
              ✓ Check IN
            </button>
          </div>
        )}

        {(action === 'working' || action.startsWith('break_end:')) && rec && (
          <>
            {/* Live status pill */}
            <div className="bg-white rounded-2xl shadow-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: '#94a3b8' }}>Checked In At</div>
                  <div className="text-2xl font-black font-mono" style={{ color: '#15803d' }}>{fmtTime(rec.check_in_at)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: '#94a3b8' }}>Breaks Used</div>
                  <div className="text-base font-bold" style={{ color: '#0f172a' }}>
                    {(rec.breaks || []).filter((b) => b.end).length} / 3
                  </div>
                </div>
              </div>

              {/* Break buttons grid */}
              <div className="grid grid-cols-3 gap-2 pt-2">
                {Object.entries(BREAK_LABELS).map(([key, label]) => {
                  const b = (rec.breaks || []).find((x) => x.type === key);
                  const done = b && b.end;
                  const open = b && !b.end;
                  return (
                    <button
                      key={key}
                      disabled={busy || done || (action.startsWith('break_end:') && !open)}
                      onClick={() => doBreak(open ? 'end' : 'start', key)}
                      data-testid={`break-${key}-btn`}
                      className="px-2 py-3 rounded-lg text-[10px] font-bold text-center disabled:opacity-40"
                      style={{
                        background: done ? '#dcfce7' : open ? '#fde68a' : '#f1f5f9',
                        color: done ? '#15803d' : open ? '#92400e' : '#334155',
                        border: open ? '2px solid #d97706' : '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ fontSize: '18px', marginBottom: 2 }}>{label.split(' ')[0]}</div>
                      <div>{done ? '✓ DONE' : open ? '⏸ END BREAK' : label.split(' ').slice(1).join(' ')}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Check-out (only when no open break) */}
            {action === 'working' && (
              <div className="bg-white rounded-2xl shadow-xl p-5 text-center space-y-2">
                <div className="text-xs" style={{ color: '#64748b' }}>Done for the day?</div>
                <button onClick={() => openSelfieFor('out')} disabled={busy} data-testid="check-out-btn"
                  className="w-full py-3.5 rounded-xl text-white font-bold text-sm uppercase tracking-wide"
                  style={{ background: '#b45309' }}>
                  ✗ Check OUT
                </button>
              </div>
            )}
          </>
        )}

        {action === 'done' && rec && (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center space-y-3">
            <div className="text-4xl">🎉</div>
            <div className="text-base font-bold" style={{ color: '#0f172a' }}>All done for today!</div>
            <div className="grid grid-cols-2 gap-3 pt-2 text-left">
              <div className="rounded-lg p-2.5" style={{ background: '#f0fdf4' }}>
                <div className="text-[9px] uppercase font-bold" style={{ color: '#15803d' }}>Check In</div>
                <div className="text-sm font-mono font-bold">{fmtTime(rec.check_in_at)}</div>
              </div>
              <div className="rounded-lg p-2.5" style={{ background: '#fef3c7' }}>
                <div className="text-[9px] uppercase font-bold" style={{ color: '#92400e' }}>Check Out</div>
                <div className="text-sm font-mono font-bold">{fmtTime(rec.check_out_at)}</div>
              </div>
              <div className="rounded-lg p-2.5" style={{ background: '#dbeafe' }}>
                <div className="text-[9px] uppercase font-bold" style={{ color: '#1e40af' }}>Work Time</div>
                <div className="text-sm font-bold">{fmtDuration(rec.work_minutes)}</div>
              </div>
              <div className="rounded-lg p-2.5" style={{ background: '#f3e8ff' }}>
                <div className="text-[9px] uppercase font-bold" style={{ color: '#6b21a8' }}>OT (pending)</div>
                <div className="text-sm font-bold">{fmtDuration(rec.ot_minutes)}</div>
              </div>
            </div>
            <div className="text-[10px]" style={{ color: '#94a3b8' }}>
              See you tomorrow at 10:30 AM
            </div>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-xl p-3 text-xs" style={{ color: '#b91c1c' }}>{error}</div>
        )}

        <div className="text-center text-[10px]" style={{ color: '#94a3b8' }}>
          Dextra Technologies · Daily Attendance Portal
        </div>
      </div>
    </div>
  );
};

export default CheckInPage;
